// How the Contact Manager builds its deck (owner, 2026-10-02: "a Contact Manager screen that acts like a dating
// app, tile based, swipe left and right, with contact opportunities listed for each contact we look at, and
// ability to begin crafting"). Pure: the outreach list comes first in the API's own order (strongest reason
// first), then everyone else off the contact list in the Contacts page's order, nobody twice, no machines, and
// anyone the rep has voted "less" at the end. No fetch, no React, no scoring — the API ranked both lists.
import type { ContactView } from '../types/contacts'
import type { Engagement, Signal } from '../types/deals'
import type { OutreachItem, OutreachNextStep, OutreachPursuit } from '../types/outreach'
import { OPPORTUNITY_PARAM } from '../pages/Workbench/opportunityFocus'
import { isMachine, rankContacts } from './contacts'

/** One tile. The same shape whichever list the person came off, so the tile reads one way. */
export interface DeckCard {
  personId: string
  name: string
  title: string | null
  company: string | null
  companyId: string | null
  photoUrl: string | null
  location: string | null
  /** 0..1 from the person's own mail (how often, how recently); null when nothing has been weighed */
  strength: number | null
  /** the newest thing on file either way, meetings included (ISO); null when nothing is */
  lastTouch: string | null
  /** the sentence the outreach list wrote, naming its own evidence; null off the contact list */
  reason: string | null
  reasonKind: string | null
  daysSinceContact: number | null
  openPursuit: OutreachPursuit | null
  nextStep: OutreachNextStep | null
  /** the rep's own vote: 1 important, -1 less, null none */
  vote: number | null
  role: string | null
  persona: string | null
  signals: { dormant: boolean; moved: boolean }
  /** which list the card came off: the outreach list carries a reason, the contact list does not */
  source: 'outreach' | 'contacts'
}

/** The outreach list first, in its order; then everyone else, ranked as the Contacts page ranks them; "less" last. */
export function buildDeck(outreach: OutreachItem[], contacts: ContactView[], referralIds: Set<string>): DeckCard[] {
  const byId = new Map<string, ContactView>()
  for (const c of contacts) byId.set(c.person.personId, c)

  const seen = new Set<string>()
  const cards: DeckCard[] = []
  for (const item of outreach) {
    const id = item.person.personId
    if (seen.has(id)) continue
    const row = byId.get(id)
    if (item.person.role === 'machine' || item.person.role === 'colleague' || (row && (isMachine(row) || row.person.role === 'colleague'))) continue   // a machine or a colleague is never a card (PL-154, PL-213)
    seen.add(id)
    cards.push(fromOutreach(item, row))
  }
  const rest = rankContacts(contacts.filter((c) => !isMachine(c) && c.person.role !== 'colleague' && !seen.has(c.person.personId)), 'matters', referralIds)
  for (const c of rest) cards.push(fromContact(c))
  return sinkLess(cards)
}

/** "Less" goes to the end of the deck, in the order it was already in; everyone else keeps their place. */
function sinkLess(cards: DeckCard[]): DeckCard[] {
  const kept = cards.filter((c) => (c.vote ?? 0) >= 0)
  const less = cards.filter((c) => (c.vote ?? 0) < 0)
  return [...kept, ...less]
}

function fromOutreach(item: OutreachItem, row: ContactView | undefined): DeckCard {
  const p = item.person
  const r = row?.person
  const sig = signalsOf(row?.signals)
  const quiet = item.reasonKind === 'warm-gone-quiet' || item.secondary.some((c) => c.kind === 'warm-gone-quiet')
  return {
    personId: p.personId,
    name: p.name,
    title: p.title ?? r?.title ?? null,
    company: item.company || row?.companyName || null,
    companyId: item.companyId ?? p.companyId ?? r?.companyId ?? null,
    photoUrl: p.photoUrl ?? r?.photoUrl ?? null,
    location: p.location ?? r?.location ?? null,
    strength: p.relationshipStrength ?? r?.relationshipStrength ?? null,
    lastTouch: newest(item.lastOutbound, item.lastInbound, item.lastMeeting) ?? row?.lastActivity ?? null,
    reason: item.reason,
    reasonKind: item.reasonKind,
    daysSinceContact: item.daysSinceContact,
    openPursuit: item.openPursuit,
    nextStep: item.nextStep,
    vote: item.vote ?? row?.vote ?? null,
    role: p.role ?? r?.role ?? null,
    persona: p.persona ?? r?.persona ?? null,
    signals: { dormant: sig.dormant || quiet, moved: sig.moved },
    source: 'outreach',
  }
}

function fromContact(c: ContactView): DeckCard {
  const p = c.person
  return {
    personId: p.personId,
    name: p.name,
    title: p.title ?? null,
    company: c.companyName || null,
    companyId: p.companyId ?? null,
    photoUrl: p.photoUrl ?? null,
    location: p.location ?? null,
    strength: p.relationshipStrength ?? null,
    lastTouch: c.lastActivity,
    reason: null,
    reasonKind: null,
    daysSinceContact: c.lastActivity ? c.dormancyDays : null,
    openPursuit: null,
    nextStep: null,
    vote: c.vote,
    role: p.role ?? null,
    persona: p.persona ?? null,
    signals: signalsOf(c.signals),
    source: 'contacts',
  }
}

function signalsOf(signals: Signal[] | undefined): { dormant: boolean; moved: boolean } {
  const list = signals ?? []
  return { dormant: list.some((s) => s.type === 'dormant'), moved: list.some((s) => s.type === 'job-change') }
}

/** The newest of several dates; an unparseable one is ignored. */
function newest(...isos: (string | null | undefined)[]): string | null {
  let best: string | null = null
  let bestAt = -Infinity
  for (const iso of isos) {
    if (!iso) continue
    const t = Date.parse(iso)
    if (!Number.isNaN(t) && t > bestAt) { best = iso; bestAt = t }
  }
  return best
}

/* ── words ── */

const KIND_WORDS: Record<string, string> = {
  'they-wrote-unanswered': 'They wrote, unanswered',
  'next-step-owed': 'A next step is owed',
  'warm-gone-quiet': 'Warm, gone quiet',
  'meeting-follow-up': 'Meeting follow-up',
  'new-lead': 'New lead',
  'overdue-reply-from-them': 'Their reply is overdue',
  'new-investment': 'New investment',
  'season-greeting': 'Season greeting',
}

/** The reason kind in words: the API's own label when the list sent one, else the house word for the kind. */
export function kindLabel(kind: string | null, labels?: ReadonlyMap<string, string>): string {
  if (!kind) return ''
  return labels?.get(kind) ?? KIND_WORDS[kind] ?? kind.replace(/-/g, ' ')
}

const STAGE_WORDS: Record<string, string> = {
  lead: 'Lead', opportunity: 'Opportunity', proposal: 'Proposal out', negotiation: 'Negotiating', won: 'Won', lost: 'Lost', delivery: 'In delivery', dormant: 'Gone quiet',
}

export function stageWord(stage: string): string {
  return STAGE_WORDS[stage] ?? stage
}

/** "12 days since you were last in touch." — the days line under the reason. */
export function sinceLine(days: number | null): string {
  if (days == null) return 'No contact on file either way.'
  if (days === 0) return 'In touch today.'
  if (days === 1) return 'In touch yesterday.'
  return `${days} days since you were last in touch.`
}

/** The strength band the bar is coloured by, as the Contacts list colours it. */
export function strengthBand(strength: number | null): 'strong' | 'warm' | 'faint' {
  const s = strength ?? 0
  return s >= 0.6 ? 'strong' : s >= 0.3 ? 'warm' : 'faint'
}

/** "12 of 340" — where the rep is in the deck; `done` is how many have been acted on. */
export function positionLine(done: number, total: number): string {
  if (total === 0) return 'Nobody on the deck'
  return `${Math.min(done + 1, total)} of ${total}`
}

/* ── where the buttons go ── */

/** The workbench on one account and one person, and the opportunity the message serves when there is one. */
export function workbenchPath(companyId: string, personId: string, engagementId: string | null = null): string {
  const base = `/workbench/${encodeURIComponent(companyId)}?person=${encodeURIComponent(personId)}`
  return engagementId ? `${base}&${OPPORTUNITY_PARAM}=${encodeURIComponent(engagementId)}` : base
}

/** A pursuit's own page: leads and the gone-quiet live under /leads, the rest under /opportunities. */
export function pursuitPage(stage: string, engagementId: string): string {
  return `/${stage === 'lead' || stage === 'dormant' ? 'leads' : 'opportunities'}/${encodeURIComponent(engagementId)}`
}

export interface CraftTarget {
  to: string
  label: 'Begin crafting' | 'Open card'
  /** the opportunity the workbench opens on: the open pursuit, else the one pursuit on file, else none */
  engagementId: string | null
}

/**
 * Where "Begin crafting" goes: the workbench on the person's account, opened on the opportunity when it is not
 * in doubt (the pursuit on the list today, or the only one on file). Without an account there is no workbench
 * to open, so the button opens the card instead and says so.
 */
export function craftTarget(card: Pick<DeckCard, 'personId' | 'companyId' | 'openPursuit'>, pursuits: readonly Pick<Engagement, 'engagementId'>[]): CraftTarget {
  if (!card.companyId) return { to: `/contacts/${encodeURIComponent(card.personId)}`, label: 'Open card', engagementId: null }
  const ids = new Set<string>(pursuits.map((p) => p.engagementId))
  if (card.openPursuit) ids.add(card.openPursuit.engagementId)
  const engagementId = card.openPursuit?.engagementId ?? (ids.size === 1 ? [...ids][0] : null)
  return { to: workbenchPath(card.companyId, card.personId, engagementId), label: 'Begin crafting', engagementId }
}

/** The pursuits on file beyond the one the outreach list already put on the tile. */
export function otherPursuits(card: Pick<DeckCard, 'openPursuit'>, pursuits: Engagement[]): Engagement[] {
  const open = card.openPursuit?.engagementId
  return open ? pursuits.filter((e) => e.engagementId !== open) : pursuits
}
